import React, { useState, forwardRef, useEffect, useRef, useCallback } from "react";
import { Responsive, Layouts } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./Grid.css";
import { GridProps, GridRef } from "./types";
import { classNames, useObserveElementWidth, useTranslate } from "helpers";
import GridWidget from "./GridWidget";
import { GRID_COLS } from "./helper";

const Grid = forwardRef<GridRef, GridProps>(
  ({ initialWidgets = [], initialLayouts, rowHeight = 90, onLayoutChange }, ref) => {
    const [layouts, setLayouts] = useState<Layouts>(
      initialLayouts ?? { lg: [] }
    );
    const [visibleWidgets, setVisibleWidgets] = useState<Set<string>>(new Set());
    // `WidthProvider` mjeri samo na mount i window.resize, pa mu promakne sklapanje
    // sidebara i kasnije slaganje stranice. ResizeObserver prati stvarni kontejner.
    const { width, ref: widthRef } = useObserveElementWidth<HTMLDivElement>();
    const { t } = useTranslate();
    const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
    // Iz helpera, da se rasporedi i mreža ne raziđu po broju stupaca.
    const cols = GRID_COLS as { [key: string]: number };

    // Widget se montira (i tek tada dohvaća podatke + animira grafove) kad uđe u viewport.
    // Overview je na vrhu pa se učitava prvi. rootMargin malo predučita prije nego postane vidljiv.
    const observerRef = useRef<IntersectionObserver | null>(null);
    const itemRefs = useRef<Map<string, HTMLElement>>(new Map());

    useEffect(() => {
      if (typeof IntersectionObserver === "undefined") {
        // Fallback (npr. bez IO podrške) — prikaži sve.
        setVisibleWidgets(new Set(initialWidgets.map((w) => w.i)));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = (entry.target as HTMLElement).dataset.widgetId;
            if (id && entry.isIntersecting) {
              setVisibleWidgets((prev) => (prev.has(id) ? prev : new Set([...prev, id])));
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "200px", threshold: 0 }
      );

      observerRef.current = observer;
      itemRefs.current.forEach((el) => observer.observe(el));

      return () => observer.disconnect();
    }, [initialWidgets]);

    const registerItem = useCallback(
      (id: string) => (el: HTMLElement | null) => {
        if (el) {
          el.dataset.widgetId = id;
          itemRefs.current.set(id, el);
          observerRef.current?.observe(el);
        } else {
          const prev = itemRefs.current.get(id);
          if (prev) observerRef.current?.unobserve(prev);
          itemRefs.current.delete(id);
        }
      },
      []
    );

    const handleLayoutChange = (_: any, allLayouts: Layouts) => {
      setLayouts(allLayouts);
      onLayoutChange?.(allLayouts); // ⬅️ javimo parentu
    };

    return (
      <div ref={widthRef} className="w-full">
        {/* Bez izmjerene širine mreža se ne crta — inače prvi render ode na pretpostavku. */}
        {width > 0 && (
          <Responsive
            className="layout"
            width={width}
            layouts={layouts}
            breakpoints={breakpoints}
            cols={cols}
            rowHeight={rowHeight}
            isResizable
            isDraggable
            draggableHandle=".drag-handle" // ⬅️ samo header je drag handle
            draggableCancel=".no-drag"
            onLayoutChange={handleLayoutChange}
          >
            {initialWidgets.map((widget) => (
              <div
                key={widget.i}
                className={classNames(
                  "relative overflow-hidden m-[-10px]",
                  widget.isActive
                    ? "ring-1 ring-slate-200 "
                    : "border border-slate-300 border-dashed"
                )}
              >
                <GridWidget
                  widget={widget}
                  isVisible={visibleWidgets.has(widget.i)}
                  registerRef={registerItem(widget.i)}
                />
              </div>
            ))}
          </Responsive>
        )}
      </div>
    );
  }
);

export default Grid;
